<template>
  <div class="register-container">
    <!-- 标题栏 -->
    <div class="section-header">
      <span class="section-tag">06</span>
      <h2 class="section-title">报名入口</h2>
    </div>

    <!-- 主体内容：左侧入口 + 右侧海报 -->
    <div class="main-content">
      <!-- 左侧入口区域 -->
      <div class="entry-area" style="padding: 20px">
        <!-- 多方协同标题 -->
        <div class="coop-title">多方协同</div>

        <!-- 公众号/专栏按钮 -->
        <div class="channel-btns">
          <button class="channel-btn" v-for="btn in props.data.channelBtns" :key="btn">
            {{ btn }}
          </button>
        </div>

        <!-- 报名类型按钮 -->
        <div class="entry-btns">
          <button
              class="entry-btn"
              v-for="btn in props.data.entryBtns"
              :key="btn.type"
              @click="openForm"
          >
            <span class="btn-icon">{{ btn.icon }}</span>
            {{ btn.type }}
          </button>
        </div>
      </div>

      <!-- 右侧海报 -->
      <div class="poster-area">
        <img
            src="https://picsum.photos/id/1082/400/500"
            alt="星途杯海报"
            class="poster-img"
        />
      </div>
    </div>

    <!-- 报名表单弹窗（点击按钮后显示） -->
    <div class="form-modal" v-if="isFormOpen">
      <div class="modal-overlay" @click="closeForm"></div>
      <div class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title">填写报名信息</h3>
          <span class="close-btn" @click="closeForm">×</span>
        </div>
        <form @submit.prevent="handleSubmit" class="modal-form">
          <!-- 表单字段 -->
          <div class="form-group" v-for="(field, idx) in props.data.formFields" :key="idx">
            <label :for="`field-${idx}`" class="form-label">
              {{ field.label }}
              <span class="required" v-if="field.required">*</span>
            </label>
            <div class="form-input">
              <input
                  v-if="field.type !== 'select'"
                  :id="`field-${idx}`"
                  :type="field.type"
                  v-model="formData[field.label]"
                  :required="field.required"
                  placeholder="请输入"
              />
              <select
                  v-else
                  :id="`field-${idx}`"
                  v-model="formData[field.label]"
                  :required="field.required"
              >
                <option value="">请选择</option>
                <option :value="opt" v-for="opt in field.options" :key="opt">
                  {{ opt }}
                </option>
              </select>
            </div>
          </div>
          <button type="submit" class="submit-btn">{{ props.data.submitText }}</button>
        </form>
      </div>
    </div>

    <!-- 提交成功弹窗 -->
    <div class="success-modal" v-if="isSuccess">
      <div class="success-content">
        <div class="success-icon">✅</div>
        <h3>报名成功！</h3>
        <p>{{ props.data.successMsg }}</p>
        <button class="confirm-btn" @click="isSuccess = false">确认</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps } from 'vue';

const props = defineProps({
  data: { type: Object, required: true },
  theme: { type: Object, required: true }
});

// 表单状态
const isFormOpen = ref(false);
const isSuccess = ref(false);
const formData = ref({});

// 打开表单
const openForm = () => {
  isFormOpen.value = true;
  formData.value = {}; // 重置表单
};

// 关闭表单
const closeForm = () => {
  isFormOpen.value = false;
};

// 提交表单
const handleSubmit = () => {
  console.log('报名信息：', formData.value);
  setTimeout(() => {
    isFormOpen.value = false;
    isSuccess.value = true;
  }, 1000);
};
</script>

<style scoped lang="scss">
// 核心变量
$primary: v-bind('props.theme.primaryColor');
$bg-light: #FFF9FC;
$text-dark: #333;
$text-gray: #666;
$radius: 8px;
$shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

// 外层容器
.register-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
}

// 标题栏
.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 32px;

  .section-tag {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: $primary;
    color: white;
    font-size: 18px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
  }

  .section-title {
    font-size: 24px;
    color: $text-dark;
    margin: 0;
  }
}

// 主体内容布局
.main-content {
  display: flex;
  gap: 32px;
  align-items: flex-start;
  flex-wrap: wrap;
}

// 左侧入口区域
.entry-area {
  flex: 2;
  min-width: 300px;
  background: $bg-light;
  border-radius: $radius;
  padding: 24px;
  box-shadow: $shadow;

  .coop-title {
    font-size: 18px;
    color: $primary;
    font-weight: 600;
    margin-bottom: 24px;
    padding-bottom: 8px;
    border-bottom: 2px solid $primary;
    width: fit-content;
  }

  .channel-btns {
    display: flex;
    gap: 16px;
    margin-bottom: 32px;
    flex-wrap: wrap;
  }

  .channel-btn {
    padding: 8px 16px;
    background: $primary;
    color: white;
    border: none;
    border-radius: 20px;
    font-size: 14px;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.9;
    }
  }

  .entry-btns {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .entry-btn {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 24px;
    background: white;
    border: 1px solid $primary;
    border-radius: $radius;
    color: $primary;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: $primary;
      color: white;
    }

    .btn-icon {
      font-size: 20px;
    }
  }
}

// 右侧海报区域
.poster-area {
  flex: 1;
  min-width: 300px;
  border-radius: $radius;
  overflow: hidden;
  box-shadow: $shadow;

  .poster-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

// 表单弹窗
.form-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;

  .modal-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
  }

  .modal-content {
    position: relative;
    background: white;
    border-radius: $radius;
    padding: 24px;
    width: 90%;
    max-width: 400px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    .modal-title {
      font-size: 18px;
      color: $text-dark;
      font-weight: 600;
    }

    .close-btn {
      font-size: 24px;
      color: $text-gray;
      cursor: pointer;
      transition: color 0.2s;

      &:hover {
        color: $primary;
      }
    }
  }

  .modal-form {
    .form-group {
      margin-bottom: 20px;

      .form-label {
        display: block;
        margin-bottom: 8px;
        font-size: 14px;
        color: $text-dark;
        .required {
          color: red;
        }
      }

      .form-input {
        input, select {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid #ddd;
          border-radius: $radius;
          font-size: 14px;

          &:focus {
            outline: none;
            border-color: $primary;
          }
        }
      }
    }

    .submit-btn {
      width: 100%;
      padding: 12px;
      background: $primary;
      color: white;
      border: none;
      border-radius: $radius;
      font-size: 16px;
      cursor: pointer;
      transition: opacity 0.2s;

      &:hover {
        opacity: 0.9;
      }
    }
  }
}

// 成功弹窗
.success-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;

  .success-content {
    background: white;
    border-radius: $radius;
    padding: 32px;
    text-align: center;
    max-width: 350px;
    width: 90%;

    .success-icon {
      font-size: 48px;
      color: #4CAF50;
      margin-bottom: 16px;
    }

    h3 {
      font-size: 20px;
      color: $text-dark;
      margin-bottom: 12px;
    }

    p {
      font-size: 14px;
      color: $text-gray;
      margin-bottom: 24px;
    }

    .confirm-btn {
      padding: 10px 24px;
      background: $primary;
      color: white;
      border: none;
      border-radius: $radius;
      cursor: pointer;
    }
  }
}

// 响应式适配
@media (max-width: 768px) {
  .main-content {
    flex-direction: column;
  }
}
</style>